import { motion } from 'motion/react';
import { Check } from 'lucide-react';

/**
 * Brief celebration moment shown once when a generation result is delivered.
 *
 * Renders a cyan checkmark with a scale-pop + fade animation that plays over
 * ~700ms, then vanishes. Non-blocking (`pointer-events: none`) so the user
 * can interact with the canvas during the animation.
 *
 * Honors `prefers-reduced-motion`: returns null when the user has opted out
 * of motion — the result image swap is already enough visual feedback.
 *
 * The component has no visibility prop of its own; the parent controls
 * mount/unmount via a local timer (see `EditorPage`).
 */

// Snapshot the media query once at module load — matches the pattern used by
// SkyrimLoading so both animations behave consistently.
const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

export function CelebrationMoment() {
  if (prefersReducedMotion) return null;

  return (
    <motion.div
      aria-hidden="true"
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '96px',
        height: '96px',
        borderRadius: '50%',
        backgroundColor: 'rgba(17, 19, 23, 0.75)',
        border: '2px solid var(--color-accent-cyan)',
        boxShadow: '0 0 24px rgba(0, 229, 255, 0.4)',
        color: 'var(--color-accent-cyan)',
        pointerEvents: 'none',
        zIndex: 30,
      }}
      data-testid="celebration-moment"
    >
      <Check size={64} strokeWidth={2.5} />
    </motion.div>
  );
}
